'use client';
import React from 'react';

import styled from '@benzinga/themetron';
import { Button, Rate } from '@benzinga/core-ui';
import { ContentManager, Entity, ReviewForm, ReviewFormItem, WordpressPost } from '@benzinga/content-manager';
import { SessionContext } from '@benzinga/session-context';
import { useIsUserLoggedIn, useUser } from '@benzinga/user-context';

export interface SubmitReviewBox {
  form: ReviewForm;
  post: WordpressPost | Entity;
  title: string;
}

export const SubmitReviewBox: React.FC<SubmitReviewBox> = ({ form, post, title }) => {
  const [rating, setRating] = React.useState<number>(0);
  const [inputs, setInputs] = React.useState<any>({});
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const user = useUser();
  const isLoggedIn = useIsUserLoggedIn();

  const session = React.useContext(SessionContext);
  const contentManager = session.getManager(ContentManager);

  const handleRatingChange = React.useCallback((rating: number) => {
    setRating(rating);
  }, []);

  React.useEffect(() => {
    const updatedInputs = inputs;
    updatedInputs['review-field-2'] = user?.displayName;
    updatedInputs['review-field-3'] = user?.email;
    setInputs(updatedInputs);
  }, [inputs, user]);

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value: string = e.target.value;
      const name: string = e.target.name;
      const updatedInputs = inputs;
      updatedInputs[name] = value;
      setInputs(updatedInputs);
    },
    [inputs],
  );

  const handleSubmission = React.useCallback(async () => {
    if (!form?.items) return;

    let requiredFieldMissing = 0;
    Object.keys(form.items).forEach(function (key) {
      const { item_id, item_type: inputType, required } = form.items[key];
      const nameKey = `${inputType}-${item_id}`;
      if (required && inputType !== 'rating-item' && typeof inputs[nameKey] === 'undefined') {
        requiredFieldMissing = 1;
      }
    });

    if (!rating) {
      requiredFieldMissing = 1;
    }

    if (requiredFieldMissing) {
      setErrorMessage('One or more required fields are missing');
    } else {
      requiredFieldMissing = 0;
      setErrorMessage('');
      contentManager
        .submitReview(form, {
          ...inputs,
          post: post?.id,
          rating: rating,
        })
        .then(res => {
          if (res?.status === 'failed' || res?.success === false) {
            setErrorMessage(res.message);
          } else {
            setSuccessMessage('Your review has been submitted.');
            const event = new CustomEvent('review_submit', { detail: { entry: res.ok.data, form } });
            window.dispatchEvent(event);
          }
        });
    }
  }, [contentManager, form, inputs, post?.id, rating]);

  return (
    <ReviewInputWrapper className="submit-review-box m-auto">
      <div className="rating-form mrp-shortcode">
        {title && <h4>{title}</h4>}
        {!successMessage && (
          <div>
            <div className="rating-item mrp">
              <Rate onClick={handleRatingChange} value={rating} />
            </div>
            <hr />
            {form.items.map((item: ReviewFormItem) => {
              if (item.item_type === 'review-field' && item.field) {
                const name = `review-field-${item.item_id}`;
                if (isLoggedIn && [2, 3].includes(item.item_id)) return null;
                return (
                  <div className="mrp review-field" key={item.item_id}>
                    <label htmlFor={name}>{item.field.label}</label>
                    <input
                      className="custom-text-input"
                      maxLength={255}
                      name={name}
                      onChange={handleInputChange}
                      placeholder={item.field.placeholder}
                      size={30}
                      type="text"
                      value={inputs[name]}
                    />
                  </div>
                );
              }
              return null;
            })}
            <hr />
            {form.items.map((item: ReviewFormItem) => {
              if (item.item_type === 'custom-field') {
                const name = `custom-field-${item.item_id}`;
                return (
                  <div className="mrp custom-field" key={item.item_id}>
                    <label htmlFor={name}>{item.field?.label}</label>
                    <input
                      className="custom-text-input"
                      maxLength={item.field.max_length}
                      name={name}
                      onChange={handleInputChange}
                      placeholder={item.field.placeholder}
                      size={30}
                      type="text"
                      value={inputs[name]}
                    />
                  </div>
                );
              }
              return null;
            })}
            <hr />
            <Button className="submit" layout="full" onClick={handleSubmission} variant="flat-light-blue">
              Submit Rating
            </Button>
            {errorMessage && <div className="error-message">{errorMessage}</div>}
          </div>
        )}
        {successMessage && <div className="success-message">{successMessage}</div>}
      </div>
    </ReviewInputWrapper>
  );
};

const ReviewInputWrapper = styled.div`
  margin: auto;
  margin-bottom: 2rem;
  h4 {
    font-size: 1.2rem;
    font-weight: bold;
  }
  .error-message {
    color: red;
    margin-top: 0.25rem;
  }
  .success-message {
    color: green;
    text-align: center;
    padding: 18px 12px;
    font-size: 24px;
  }
  .rating-form {
    display: block;
    border-radius: 8px;
    padding: 16px;
    box-shadow: none;
    hr {
      border: 1px solid #e1ebfa;
      margin-top: 16px;
      &:first-of-type {
        margin-top: 6px;
      }
      &:last-of-type {
        display: none;
      }
    }
  }
  .rating-item {
    .star {
      font-size: 28px;
    }
  }
  .custom-text-input {
    width: 100%;
    border-radius: 4px;
    border-width: 1px;
    padding: 10px 8px;
  }
  label {
    display: block;
    margin-top: 0.8em;
    margin-bottom: 0.2em;
    text-transform: uppercase;
    font-weight: 700;
  }
  .submit {
    margin-top: 1rem;
    text-transform: uppercase;
    text-align: center;
    width: 100%;
  }
`;
